import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

// Pages
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/admin/Dashboard";
import ContentManagement from "@/pages/admin/ContentManagement";
import UserManagement from "@/pages/admin/UserManagement";
import CampaignManager from "@/pages/admin/CampaignManager";
import PredictionsCenter from "@/pages/admin/PredictionsCenter";
import AnalyticsDashboard from "@/pages/admin/AnalyticsDashboard";
import ConversionFunnel from "@/pages/admin/ConversionFunnel";
import Approvals from "@/pages/admin/Approvals";
import AutoSms from "@/pages/admin/AutoSms";
import LiveOps from "@/pages/admin/LiveOps";
import Studio from "@/pages/admin/Studio";
import PlatformSettings from "@/pages/admin/PlatformSettings";
import ArticlePage from "@/pages/ArticlePage";
import PredictionsPage from "@/pages/PredictionsPage";
import MatchCenterPage from "@/pages/MatchCenterPage";
import ScoreboardPage from "@/pages/ScoreboardPage";
import NewsPage from "@/pages/NewsPage";
import FixturePreviewPage from "@/pages/FixturePreviewPage";
import MatchManagement from "@/pages/admin/MatchManagement";
import NewsFeedCuration from "@/pages/admin/NewsFeedCuration";
import ContentHub from "@/pages/admin/ContentHub";
import SocialSources from "@/pages/admin/SocialSources";
import SchedulesManagement from "@/pages/admin/SchedulesManagement";
import CustomerBases from "@/pages/admin/CustomerBases";
import Subscriptions from "@/pages/admin/Subscriptions";
import StandingsPage from "@/pages/StandingsPage";
import BettingPredictionsPage from "@/pages/BettingPredictionsPage";
import UtabiriWanguPage from "@/pages/UtabiriWanguPage";
import PredictionPush from "@/pages/admin/PredictionPush";
import SmsInteractions from "@/pages/admin/SmsInteractions";
import ForYouPulse from "@/pages/admin/ForYouPulse";
import Customers from "@/pages/admin/Customers";
import Transactions from "@/pages/admin/Transactions";
import VisitTracker from "@/components/VisitTracker";
import { SlipProvider } from "@/contexts/SlipContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import SlipDrawer from "@/components/SlipDrawer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem("dimbani_token"));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            if (token) {
                try {
                    const response = await axios.get(`${API}/auth/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setUser(response.data);
                } catch (error) {
                    console.error("Auth init failed:", error);
                    localStorage.removeItem("dimbani_token");
                    setToken(null);
                }
            }
            setLoading(false);
        };
        initAuth();
    }, [token]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem("dimbani_token", access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, full_name, role = "user") => {
        const response = await axios.post(`${API}/auth/register`, {
            email, password, full_name, role
        });
        const { access_token, user: userData } = response.data;
        localStorage.setItem("dimbani_token", access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem("dimbani_token");
        setToken(null);
        setUser(null);
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        isAdmin: user?.role === "admin",
        isContentManager: ["admin", "subadmin", "supervisor", "content_manager"].includes(user?.role || ""),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireContentManager = false }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse text-primary text-2xl dimbani-wordmark">DIMBANI</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (requireAdmin && user.role !== "admin") {
        return <Navigate to="/" replace />;
    }

    if (requireContentManager && !["admin", "subadmin", "supervisor", "content_manager"].includes(user.role)) {
        return <Navigate to="/" replace />;
    }

    return children;
};

function App() {
    return (
        <AuthProvider>
            <SlipProvider>
                <div className="App min-h-screen bg-background">
                    <BrowserRouter>
                        <ThemeProvider>
                        <VisitTracker />
                        <Routes>
                        {/* Public Routes (Swahili) */}
                        <Route path="/" element={<HomePage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/makala/:id" element={<ArticlePage />} />
                        <Route path="/utabiri" element={<BettingPredictionsPage />} />
                        <Route path="/utabiri-classic" element={<PredictionsPage />} />
                        <Route path="/utabiri-wangu" element={<UtabiriWanguPage />} />
                        <Route path="/mechi" element={<Navigate to="/matokeo?window=upcoming" replace />} />
                        <Route path="/matokeo" element={<ScoreboardPage />} />
                        <Route path="/habari-za-dunia" element={<NewsPage />} />
                        <Route path="/ratiba/:fixtureId" element={<FixturePreviewPage />} />
                        <Route path="/msimamo" element={<StandingsPage />} />
                        
                        {/* Admin Routes (English) */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <AdminDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/content"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <ContentManagement />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/matches"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <MatchManagement />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/news-feed"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <NewsFeedCuration />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/content-hub"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <ContentHub />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/social-sources"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <SocialSources />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/schedules"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <SchedulesManagement />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/customer-bases"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <CustomerBases />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/subscriptions"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <Subscriptions />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/users"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <UserManagement />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/campaigns"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <CampaignManager />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/predictions"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <PredictionsCenter />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/prediction-push"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <PredictionPush />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/sms-interactions"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <SmsInteractions />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/for-you"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <ForYouPulse />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/customers"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <Customers />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/transactions"
                            element={
                                <ProtectedRoute requireContentManager>
                                    <Transactions />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/analytics"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AnalyticsDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/conversion-funnel"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <ConversionFunnel />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/approvals"
                            element={
                                <ProtectedRoute>
                                    <Approvals />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/auto-sms"
                            element={
                                <ProtectedRoute>
                                    <AutoSms />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/live-ops"
                            element={
                                <ProtectedRoute>
                                    <LiveOps />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/studio"
                            element={
                                <ProtectedRoute>
                                    <Studio />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/settings"
                            element={
                                <ProtectedRoute>
                                    <PlatformSettings />
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                    </ThemeProvider>
                </BrowserRouter>
                <SlipDrawer />
                <Toaster position="top-right" richColors />
            </div>
            </SlipProvider>
        </AuthProvider>
    );
}

export default App;
